import styled from "@emotion/styled";
import type { OpenbookOpenOrder } from "@stepfinance/dex";
import type { StepColumnsType } from "@stepfinance/react";
import {
  CheckboxFilter,
  ContextButton,
  getColumnSearchProps,
  greenTag,
  IconTextContainer,
  imageFromPlatformName,
  makeSimpleFilters,
  redTag,
  StepTable,
  TagInfo,
  TokenIconText,
  URLIcon,
} from "@stepfinance/react";
import type { TablePaginationConfig } from "antd";
import type { FilterValue } from "antd/lib/table/interface";
import type { FC } from "react";
import { useEffect, useState } from "react";

import { useEnrichedWallet } from "../../../../contexts/enrichedWallet";
import { useLanguageContext } from "../../../../contexts/language/language";
import { useCancelOpenbookOrders } from "../../../../hooks/mutations/openbook/useCancelOpenbookOrders";
import { isPaginationHidden, useLocalStorageState } from "../../../../utils/utils";
import RedirectLink from "../../../RedirectLink/RedirectLink";
import { PAGE_SIZE_OPTIONS } from "../../../shared";
import CellValue from "../../../Table/CellValue";
import { TokenIcon } from "../../../TokenIcon";
import { ChevronCollapse } from "../../ChevronCollapse/ChevronCollapse";

interface OpenOrdersTable {
  orders: OpenbookOpenOrder[];
  openOrdersValue: number | null;
}

export const OpenOrdersTable: FC<OpenOrdersTable> = ({ orders, openOrdersValue }) => {
  const { watching } = useEnrichedWallet();
  const [filteredInfo, setFilteredInfo] = useState<Record<string, FilterValue | null>>({});
  const [isCancelAllLoading, setIsCancelAllLoading] = useState(false);
  const [cancelId, setCancelId] = useState<string | null>(null);
  const [pagination, setPagination] = useState<TablePaginationConfig>({});
  const [paginationSetting, setPaginationSetting] = useLocalStorageState(
    "openOrdersPaginationSizeSetting",
    PAGE_SIZE_OPTIONS.m[0],
  );

  const {
    currentLanguageData: {
      common,
      components: {
        table: { headers },
      },
      pages: {
        dashboard: {
          modules: { DEXModule },
        },
      },
    },
  } = useLanguageContext();

  const cancelOrdersMutation = useCancelOpenbookOrders();

  useEffect(() => {
    setPagination({
      hideOnSinglePage: isPaginationHidden(orders.length, PAGE_SIZE_OPTIONS.m),
      showSizeChanger: true,
      pageSizeOptions: PAGE_SIZE_OPTIONS.m,
      pageSize: parseInt(paginationSetting),
    });
  }, [orders.length, paginationSetting]);

  const columns: StepColumnsType<OpenbookOpenOrder> = [
    {
      title: common.platform,
      dataIndex: "platform",
      key: "platform",
      filters: makeSimpleFilters(orders.map((order) => order.kind)),
      filterDropdown: (props) => <CheckboxFilter {...props} />,
      onFilter: (value, record) => record.kind.startsWith(value as string),
      filteredValue: filteredInfo.platform,
      render: (_, record) => (
        <IconTextContainer>
          <URLIcon src={imageFromPlatformName(record.kind)} />
          <TokenIconText bold>
            <CellValue cellType="shortenedText">{record.kind}</CellValue>
          </TokenIconText>
        </IconTextContainer>
      ),
      sorter: (accountA, accountB) => accountA.kind.localeCompare(accountB.kind),
    },
    {
      title: common.market,
      dataIndex: "market",
      key: "market",
      sorter: (a, b) => {
        return a.market.localeCompare(b.market);
      },
      ...getColumnSearchProps((record) => record.market, "market"),
      filteredValue: filteredInfo.market,
      render: (_, record) => (
        <MarketContainer>
          <TokenIcon mintAddress={[record.baseMint, record.quoteMint]} />
          <TokenIconText>
            <RedirectLink href={`https://dex.solape.io/#/market/${record.marketAddress.toBase58()}`}>
              {record.market}
            </RedirectLink>
          </TokenIconText>
        </MarketContainer>
      ),
    },
    {
      title: common.side,
      dataIndex: "side",
      key: "side",
      className: "column-center",
      render: (side) => {
        const tagColor = side === "buy" ? greenTag : redTag;
        const tagText = side === "buy" ? DEXModule.positionTypes.buy : DEXModule.positionTypes.sell;

        return (
          <div>
            <TagInfo useSpaceMono={false} tagType="text" tagColorOverride={tagColor}>
              {tagText}
            </TagInfo>
          </div>
        );
      },
      width: 110,
      filters: [
        {
          text: "Buy",
          value: "buy",
        },
        {
          text: "Sell",
          value: "sell",
        },
      ],
      filterDropdown: (props) => <CheckboxFilter {...props} />,
      onFilter: (value, record) => record.side.startsWith(value as "buy" | "sell"),
      filteredValue: filteredInfo.side,
    },
    {
      title: common.amount,
      dataIndex: "size",
      key: "size",
      className: "column-number",
      sorter: (a, b) => b.size - a.size,
      render(_, record) {
        return <CellValue cellType="number">{record.size}</CellValue>;
      },
    },
    {
      title: headers.price,
      dataIndex: "price",
      key: "price",
      className: "column-number",
      sorter: (a, b) => (a.priceInUSD ?? 0) - (b.priceInUSD ?? 0),
      render(_, record) {
        const price = record.priceInUSD;
        return (
          <CellValue cellType="value" fontWeight="regular">
            {price}
          </CellValue>
        );
      },
    },
    {
      title: () => (
        <CloseColumnContainer>
          {!watching && (
            <CancelAllContainer>
              <ContextButton
                disabled={isCancelAllLoading}
                onClick={(event) => {
                  event.stopPropagation();
                  setIsCancelAllLoading(true);
                  void cancelOrdersMutation.mutateAsync(orders).finally(() => setIsCancelAllLoading(false));
                }}
                style={{ background: "#141414" }}
              >
                {DEXModule.buttons.cancelAll}
              </ContextButton>
            </CancelAllContainer>
          )}
          <ValueTitle>{common.value}</ValueTitle>
        </CloseColumnContainer>
      ),
      dataIndex: "valueInUSD",
      key: "valueInUSD",
      className: "column-number",
      sorter: (a, b) => (a.valueInUSD ?? 0) - (b.valueInUSD ?? 0),
      defaultSortOrder: "descend",
      render(_, record) {
        return (
          <ValueContainer>
            {!watching && (
              <CancelContainer>
                <ContextButton
                  disabled={cancelId === record.orderId.toString()}
                  onClick={() => {
                    setCancelId(record.orderId.toString());
                    void cancelOrdersMutation.mutateAsync([record]).finally(() => setCancelId(null));
                  }}
                >
                  {DEXModule.buttons.cancel}
                </ContextButton>
              </CancelContainer>
            )}
            <ValueText>
              <CellValue fontWeight="bold" cellType="value">
                {record.valueInUSD}
              </CellValue>
            </ValueText>
          </ValueContainer>
        );
      },
    },
  ];

  return (
    <>
      <ChevronCollapse isCollapseChild title={DEXModule.openOrders} value={openOrdersValue}>
        <StepTable
          rowKey={(record) => `${record.market}-${record.orderId.toString()}`}
          onChange={(pagination, filters) => {
            setPaginationSetting(pagination.pageSize ? pagination.pageSize.toString() : PAGE_SIZE_OPTIONS.m[0]);
            setFilteredInfo(filters);
          }}
          columns={columns}
          pagination={pagination}
          dataSource={orders}
        />
      </ChevronCollapse>
    </>
  );
};

export const ValueContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const CancelContainer = styled.div`
  margin-right: 8px;
`;

export const ValueText = styled.span`
  width: 110px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

export const ValueTitle = styled.span`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const MarketContainer = styled.div`
  display: flex;
`;

export const CancelAllContainer = styled.div`
  margin-right: 43px;
`;

export const CloseColumnContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;
